import api from 'api';
import { AxiosResponse } from 'axios';
import { Button } from 'components/Base';
import LoaderSpinner from 'components/LoaderSpinner/LoaderSpinner';
import React, { useEffect, useState } from 'react';
import { MetaType } from 'store/modules/Meta/types';
import RiskMapTable from './componentsv2/RiskMapTable';
import { CipRiskDataType } from './types';
import CreateNewFolderIcon from '@material-ui/icons/CreateNewFolderOutlined';
import { useHistory } from 'react-router-dom';
import ReadOnlyAlert from 'components/Base/Alert/ReadOnlyAlert';
import { connect } from 'react-redux';
import selectors from 'store/selectors';

type RiskMappingV2Props = {
  isFirmAdmin: boolean;
};

const RiskMappingV2: React.FC<RiskMappingV2Props> = ({ isFirmAdmin }) => {
  const [riskMap, setRiskMap] = useState({} as CipRiskDataType);
  const [cipThresholds, setCipThresholds] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentStatuses, setCurrentStatuses] = useState([]);

  useEffect(() => {
    if (
      !isLoading &&
      currentStatuses.every(status => ['failed', 'succeeded'].includes(status))
    ) {
      return;
    }

    const interval = setInterval(() => {
      api
        .getCipThresholds()
        .then((cipResponse: AxiosResponse) => {
          setCipThresholds(cipResponse.data);
          const newStatuses = cipResponse.data.map((item: any) => item.status);
          const shouldUpdateRiskMap =
            JSON.stringify(currentStatuses) !== JSON.stringify(newStatuses) &&
            newStatuses.includes('succeeded');
          setCurrentStatuses(newStatuses);
          if (isLoading || shouldUpdateRiskMap) {
            api
              .getCipThresholdRiskMap()
              .then((riskMapResponse: AxiosResponse) => {
                setRiskMap(riskMapResponse.data);
              })
              .finally(() => {
                setIsLoading(false);
              });
          }
        })
        .catch(() => {
          setIsLoading(false);
        });
    }, 5000);

    return () => clearInterval(interval);
  }, [isLoading, currentStatuses]);

  const riskMapSelected = async (values: number[]) => {
    const clonedCipThresholds = JSON.parse(JSON.stringify(cipThresholds));
    values.forEach(value => {
      const match = clonedCipThresholds.find(
        (cipThreshold: MetaType) => cipThreshold.value === value.toString()
      );

      if (match) {
        match.status = 'pending';
      }
    });
    setCipThresholds(clonedCipThresholds);
    setCurrentStatuses(clonedCipThresholds.map((item: any) => item.status));
    return api.riskMapCip({ cip_thresholds: values });
  };

  const history = useHistory();

  return (
    <div>
      <>
        {isLoading ? (
          <LoaderSpinner isLoading={isLoading} />
        ) : (
          <>
            {!isFirmAdmin && (
              <div style={{ marginTop: '1rem' }}>
                <ReadOnlyAlert />
              </div>
            )}
          </>
        )}
        {!isLoading && cipThresholds.length === 0 && (
          <div
            style={{
              textAlign: 'center',
              marginTop: '2rem'
            }}
          >
            <CreateNewFolderIcon
              htmlColor="#6b7280"
              style={{ width: '36px', height: '36px' }}
            />
            <div style={{ fontWeight: 600 }}>
              No investment solutions available for risk mapping
            </div>
            <Button
              type="submit"
              onClick={() => history.push('/cip-management')}
              style={{ marginTop: '1rem' }}
            >
              Manage CIP
            </Button>
          </div>
        )}
        {!isLoading &&
          Object.values(riskMap).length !== 0 &&
          cipThresholds.length !== 0 &&
          Object.entries(riskMap).map((cip: any) => {
            const cipThreshold = cipThresholds.find(
              (ct: MetaType) => ct.value === cip[0]
            ) || { name: '', value: '' };
            return (
              <RiskMapTable
                key={`Risk mapping table for ${cipThreshold.name}`}
                cip={cipThreshold}
                riskMapData={cip[1]}
                riskMapSelected={riskMapSelected}
              />
            );
          })}
      </>
    </div>
  );
};

export default connect((state: any) => ({
  isFirmAdmin: selectors.advisorSelectors.isFirmAdmin(state)
}))(RiskMappingV2);
